/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Injectable } from '@angular/core';
import { createExternal3aDocuments } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import * as ProfileActions from './profile.actions';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { IsoCodes } from '@shared/models/Account';
import { GrowDocument, NewDocType, PersonalDetails } from '@shared/models/Profile';
import { AccountOpeningService } from '@shared/services/account-opening.service';
import { LanguageService, Languages } from '@shared/services/language.service';
import { ProfileService } from '@shared/services/profile.service';
import { SharedService } from '@shared/services/shared.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';

@Injectable()
export class ProfileEffects {
  getPersonalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.getPersonalDetails),
      mergeMap(() =>
        this.profileService.getPersonalInfo().pipe(
          map((response: { data: { attributes: PersonalDetails } }) =>
            ProfileActions.getPersonalDetailsSuccess({ personalDetails: response.data.attributes })
          ),
          catchError(() => this.effectsCatchError(false))
        )
      )
    )
  );

  fetchCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.fetchCountries),
      switchMap(() => {
        return this.accountOpeningService.getIsoCodes().pipe(
          map((response: IsoCodes) => {
            return ProfileActions.fetchCountriesSuccess({ countries: response.countries_translated });
          }),
          catchError(() => this.effectsCatchError(false))
        );
      })
    )
  );

  updateCustomerPhoneAndSend2FACode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateCustomerPhoneAndSend2FACode),
      mergeMap(({ personalDetails, authToken }) =>
        this.profileService.updatePersonalInfo(personalDetails).pipe(
          mergeMap((response: { data: { attributes: PersonalDetails } }) => [
            ProfileActions.send2FAAuthorizationCode({ authToken, redirect: 'verify' }),
            // TODO: fetchCustomer() - Let's align why do we have this here!
            AppRootActions.fetchCustomer(),
            // ---
            ProfileActions.getPersonalDetailsSuccess({ personalDetails: response.data.attributes })
          ]),
          catchError(() => {
            this.showToastMessage('personal.flash.error_rev');
            return of(ProfileActions.update2FALoading({ loading: false }));
          })
        )
      )
    )
  );

  send2FAAuthorizationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.send2FAAuthorizationCode),
      mergeMap(({ authToken }) =>
        this.profileService.send2FAToken(authToken).pipe(
          mergeMap(() => [ProfileActions.update2FACurrentScreen({ screen: 'verify' }), ProfileActions.update2FALoading({ loading: false })]),
          catchError(() => of(ProfileActions.update2FALoading({ loading: false })))
        )
      )
    )
  );

  toggle2FAAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.toggle2FAAuthorization),
      mergeMap(({ code }) =>
        this.profileService.toggle2FAAuthorization(code).pipe(
          mergeMap(() => [
            // TODO: fetchCustomer() - Let's align why do we have this here!
            AppRootActions.fetchCustomer(),
            // ---
            ProfileActions.update2FACurrentScreen({ screen: 'main' }),
            ProfileActions.update2FALoading({ loading: false })
          ]),
          catchError(() => {
            this.showToastMessage('2fa.code.error');
            return of(ProfileActions.update2FALoading({ loading: false }));
          })
        )
      )
    )
  );

  updateProfileAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileAddress),
      map((action) => action.personalDetails),
      switchMap((personalDetails: Partial<PersonalDetails>) =>
        this.profileService.updatePersonalInfo(personalDetails).pipe(
          map(({ data }: { data: { attributes: Partial<PersonalDetails> } }) => {
            this.showToastMessage('profile.personalDetails.success.address', 'success');
            return ProfileActions.updateProfileAddressSuccess({ personalDetails: data.attributes });
          }),
          catchError(() => {
            this.showToastMessage('personal.flash.error_rev');
            return of(ProfileActions.updateProfileAddressFailed());
          })
        )
      )
    )
  );

  updateProfileEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileEmail),
      map((action) => action.personalDetails),
      switchMap((personalDetails: Partial<PersonalDetails>) =>
        this.profileService.updatePersonalInfo(personalDetails).pipe(
          map(({ data }: { data: { attributes: Partial<PersonalDetails> } }) => {
            this.showToastMessage('profile.personalDetails.success.email', 'success');
            return ProfileActions.updateProfileEmailSuccess({ personalDetails: data.attributes });
          }),
          catchError(() => {
            this.showToastMessage('personal.flash.error_rev');
            return of(ProfileActions.updateProfileEmailFailed());
          })
        )
      )
    )
  );

  updateProfileMobilePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileMobilePhone),
      map((action) => ({
        personalDetails: action.personalDetails,
        has3AAccountMinProspect: action.has3AAccountMinProspect,
        twoFAEnabled: action.twoFAEnabled
      })),
      switchMap(
        ({
          personalDetails,
          has3AAccountMinProspect,
          twoFAEnabled
        }: {
          personalDetails: Partial<PersonalDetails>;
          has3AAccountMinProspect: boolean;
          twoFAEnabled: boolean;
        }) =>
          this.profileService.updatePersonalInfo(personalDetails).pipe(
            map(({ data }: { data: { attributes: Partial<PersonalDetails> } }) => {
              return ProfileActions.updateProfileMobilePhoneSuccess({
                personalDetails: data.attributes,
                has3AAccountMinProspect,
                twoFAEnabled
              });
            }),
            catchError(() => {
              this.showToastMessage('personal.flash.error_rev');
              return of(ProfileActions.updateProfileMobilePhoneFailed());
            })
          )
      )
    )
  );

  updateProfileOwnerName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileOwnerName),
      map((action) => ({
        accountId: action.accountId,
        ownerName: action.ownerName
      })),
      switchMap(({ accountId, ownerName }: { accountId: string; ownerName: string }) =>
        this.profileService.updateOwnerName(accountId, ownerName).pipe(
          map(() => {
            this.showToastMessage('profile.I4K.update.success', 'success');
            return ProfileActions.updateProfileOwnerSuccess({ accountId, ownerName });
          }),
          catchError((error) => {
            this.showToastMessage('personal.flash.error_rev');
            return of(ProfileActions.updateProfileOwnerFailed({ error }));
          })
        )
      )
    )
  );

  show2FAmodalAfterPhoneUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileMobilePhoneSuccess),
      map((action) => action.twoFAEnabled),
      switchMap((twoFAEnabled: boolean) => {
        if (!twoFAEnabled) {
          return EMPTY;
        }
        return of(ProfileActions.updateShow2FADisableModal({ show: true }));
      })
    )
  );

  updateProfileMobilePhoneSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileMobilePhoneSuccess),
      map((action) => action.has3AAccountMinProspect),
      switchMap((has3AAccountMinProspect: boolean) => {
        if (!has3AAccountMinProspect) {
          this.showToastMessage('profile.personalDetails.success.phoneNumber', 'success');
          return of(ProfileActions.updatePersonalDetailsCurrentScreen({ screen: 'main' }));
        }
        return of(ProfileActions.sendOtpCode());
      })
    )
  );

  sendOtpCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.sendOtpCode),
      switchMap(() =>
        this.sharedService.sendVerificationCode().pipe(
          map(() => {
            return ProfileActions.sendOtpCodeSuccess();
          }),
          catchError(() => {
            this.showToastMessage('personal.flash.error_rev');
            return of(ProfileActions.sendOtpCodeFailed());
          })
        )
      )
    )
  );

  validateMobilePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.validateMobilePhone),
      map((action) => action.code),
      switchMap((code: string) =>
        this.sharedService.verifyVerificationCode(code).pipe(
          map(() => {
            this.showToastMessage('profile.personalDetails.success.phoneNumber', 'success');
            return ProfileActions.validateMobilePhoneSuccess();
          }),
          catchError(() => of(ProfileActions.validateMobilePhoneFailed()))
        )
      )
    )
  );

  updateProfileEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileEmailSuccess),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCustomer))]),
      map(([{ personalDetails }, customer]) => {
        return AppRootActions.fetchCustomerSuccess({ customer: { ...customer, email: personalDetails.email }, selectActiveAccount: false });
      })
    )
  );

  updateProfileOwnerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileOwnerSuccess),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount))]),
      mergeMap(([{ accountId, ownerName }, { customer, currentAccountDetail }]) => {
        const actions: any[] = [
          AppRootActions.fetchCustomerSuccess({
            customer: {
              ...customer,
              accounts: customer.accounts.map((account) => {
                if (account.id === accountId) {
                  return { ...account, owner_name: ownerName };
                }

                return account;
              })
            },
            selectActiveAccount: false
          })
        ];

        // if the active account is the same kid account update header
        if (currentAccountDetail.id === accountId) {
          actions.push(
            AppRootActions.fetchCustomerAccountDetailSuccess({
              customerAccountDetail: { ...currentAccountDetail, owner_name: ownerName }
            })
          );
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return actions;
      })
    )
  );

  updateProfileGetPersonalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileMobilePhoneSuccess, ProfileActions.validateMobilePhoneSuccess),
      switchMap(() => [
        // TODO: fetchCustomer() - Let's align why do we have this here!
        AppRootActions.fetchCustomer(),
        // ---
        ProfileActions.getPersonalDetails()
      ])
    )
  );

  updateCustomerLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateCustomerLanguage),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount))]),
      mergeMap(([{ personalDetails }, { customer }]) =>
        this.profileService.updatePersonalInfo(personalDetails).pipe(
          mergeMap((response: { data: { attributes: PersonalDetails } }) => {
            const { attributes: personalDetails } = response.data;
            const { language } = personalDetails;

            this.languageService.setLanguage(language as Languages);

            setTimeout(() => {
              this.showToastMessage('profile.personalDetails.success.language', 'success');
            }, 100);

            return [
              AppRootActions.fetchCustomerSuccess({
                customer: { ...customer, language },
                selectActiveAccount: false
              }),
              ProfileActions.getPersonalDetailsSuccess({ personalDetails }),
              AppRootActions.setIsLanguageChange({ change: true })
            ];
          }),
          catchError(() => this.effectsCatchError())
        )
      )
    )
  );

  validatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.validatePassword),
      map((action) => action.password),
      switchMap((password: string) =>
        this.profileService.validatePassword({ password }).pipe(
          map(() => ProfileActions.validatePasswordSuccess()),
          catchError(() => {
            this.showToastMessage('2fa.password_verification.error');
            return of(ProfileActions.validatePasswordFailed());
          })
        )
      )
    )
  );

  validatePassword2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.validatePassword2FA),
      mergeMap(({ password, enabled2FA, authToken }) =>
        this.profileService.validatePassword({ password }).pipe(
          mergeMap(() => {
            if (enabled2FA) {
              return [ProfileActions.send2FAAuthorizationCode({ authToken, redirect: 'verify' })];
            }
            return [ProfileActions.update2FACurrentScreen({ screen: 'phone' }), ProfileActions.update2FALoading({ loading: false })];
          }),
          catchError(() => {
            this.showToastMessage('2fa.password_verification.error');
            return of(ProfileActions.update2FALoading({ loading: false }));
          })
        )
      )
    )
  );

  updateProfilePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfilePassword),
      mergeMap(({ currentPassword, newPassword, newPasswordConfirmation }) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        this.profileService
          .updatePassword({ current_password: currentPassword, password: newPassword, password_confirmation: newPasswordConfirmation })
          .pipe(
            map((response) => {
              this.showToastMessage('profile.personalDetails.success.password', 'success');
              return ProfileActions.updateProfilePasswordStatus({ status: response.status });
            }),
            catchError((err) => {
              if (!err.error || !err.error.errors) {
                this.showToastMessage('personal.flash.error');
                return of(ProfileActions.updateProfilePasswordStatus({ status: err.error.status }));
              }
              // eslint-disable-next-line no-prototype-builtins
              if (err.error.errors.hasOwnProperty('current_password')) {
                this.showToastMessage('personal.flash.error_current_password');
                // eslint-disable-next-line no-prototype-builtins
              } else if (err.error.errors.hasOwnProperty('password_confirmation')) {
                this.showToastMessage('personal.flash.error_password');
              } else {
                this.showToastMessage('personal.flash.error');
              }
              return of(ProfileActions.updateProfilePasswordStatus({ status: 'error' }));
            })
          )
      )
    )
  );

  fetchCustomerDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.fetchCustomerDocs),
      mergeMap(() =>
        this.profileService.getCustomerDocuments().pipe(
          map((response: { data: NewDocType[]; meta: [] }) => ProfileActions.fetchCustomerDocsSuccess({ documents: response.data })),
          catchError(() => of(ProfileActions.fetchCustomerDocsFail()))
        )
      )
    )
  );

  fetchLibertyDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.fetchLibertyDocs),
      mergeMap(({ perItem, page, sort, filter }) =>
        this.profileService.getLibertyDocuments(perItem, page, sort, filter).pipe(
          map((response) => {
            return ProfileActions.fetchLibertyDocsSuccess({
              docs: response.data,
              total: response.meta.stats.total.count,
              pageIndex: page,
              filter,
              sort
            });
          }),
          catchError(() => {
            return of(ProfileActions.fetchLibertyDocsFail());
          })
        )
      )
    )
  );

  fetchGrowDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.fetchGrowDocs),
      mergeMap(() =>
        this.profileService.getGrowDocuments().pipe(
          map((response: { data: GrowDocument[]; meta: [] }) => ProfileActions.fetchGrowDocsSuccess({ documents: response.data })),
          catchError(() => of(ProfileActions.fetchGrowDocsFail()))
        )
      )
    )
  );

  fetchStaticDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.fetchStaticDocs),
      concatLatestFrom(() => this.store.select(fromAppRoot.selectCustomer)),
      mergeMap(([, customer]) =>
        this.profileService.getStaticDocuments().pipe(
          map((res) => {
            const external3aDocs = createExternal3aDocuments(customer, (key) => this.translateService.instant(key));
            const documents = [...res.data.filter((doc) => doc.attributes.account_type !== '3a'), ...external3aDocs];
            return ProfileActions.fetchStaticDocsSuccess({ documents });
          }),
          catchError(() => of(ProfileActions.fetchStaticDocsFail()))
        )
      )
    )
  );

  getChCity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.getChCity),
      map((action) => action.zipCode),
      switchMap((zipCode: string) =>
        this.profileService.getChCitiesByZip(zipCode).pipe(map((response) => ProfileActions.getChCitySuccess({ response })))
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      catchError((error) => of(ProfileActions.getChCityFailed(error)))
    )
  );

  private showToastMessage = (key: string, type: 'critical' | 'success' = 'critical') => {
    this.toastNotificationService.show(type, this.translateService.instant(key) as string);
  };

  private effectsCatchError = (showToastMesage = true) => {
    if (showToastMesage) {
      this.showToastMessage('personal.flash.error_rev');
    }
    return EMPTY;
  };

  constructor(
    private actions$: Actions,
    private store: Store,
    private sharedService: SharedService,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService,
    private accountOpeningService: AccountOpeningService,
    private profileService: ProfileService,
    private languageService: LanguageService
  ) {}
}
